import {
  expectArray,
  expectBoolean,
  expectRecord,
  expectString,
  parseGenerateStudentBillingMonthlyDraftRequest,
  parsePutStudentBillingEventChargeTargetRequest,
  parseStudentBillingIdentifier,
  parseStudentBillingMonthlyGeneration,
  parseStudentBillingMonthlyUnit,
  parseStudentBillingMonthlyWarning,
  parseStudentBillingServiceMonth,
  type GenerateStudentBillingMonthlyDraftRequest,
  type PutStudentBillingEventChargeTargetRequest,
  type StudentBillingEventChargeTargetPreviewDto,
  type StudentBillingMonthlyListDto,
  type StudentBillingMonthlyUnit
} from "@madamuh/domain";
import { apiGet, apiPostWithHeaders, apiPutWithHeaders } from "./client";

const exact = (record: Record<string, unknown>, allowed: readonly string[], path: string) => {
  const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
  if (unknown.length) throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
};
const headers = () => ({ "Idempotency-Key": crypto.randomUUID() });
const root = (tenantSlug: string) => `/api/v1/admin/tenants/${encodeURIComponent(tenantSlug)}/billing`;
const positiveInteger = (value: unknown, path: string) => {
  if (!Number.isSafeInteger(value) || Number(value) < 1) throw new Error(`${path} is invalid`);
  return Number(value);
};
const fingerprint = (value: unknown, path: string) => {
  const parsed = expectString(value, path);
  if (!/^[a-f0-9]{64}$/u.test(parsed)) throw new Error(`${path} is invalid`);
  return parsed;
};

function capabilities(value: unknown) {
  const record = expectRecord(value, "monthly.capabilities");
  exact(record, ["canReadMonthlyDrafts", "canGenerateMonthlyDrafts"], "monthly.capabilities");
  if (record.canReadMonthlyDrafts !== true) throw new Error("monthly read capability is invalid");
  return { canReadMonthlyDrafts: true as const, canGenerateMonthlyDrafts: expectBoolean(record.canGenerateMonthlyDrafts, "monthly.capabilities.canGenerateMonthlyDrafts") };
}

function monthly(value: unknown): StudentBillingMonthlyListDto {
  const record = expectRecord(value, "monthly");
  exact(record, ["capabilities", "serviceMonth", "generation", "stale", "globalWarnings", "units"], "monthly");
  return {
    capabilities: capabilities(record.capabilities),
    serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth, "monthly.serviceMonth"),
    generation: record.generation === null ? null : parseStudentBillingMonthlyGeneration(record.generation, "monthly.generation"),
    stale: expectBoolean(record.stale, "monthly.stale"),
    globalWarnings: expectArray(record.globalWarnings, parseStudentBillingMonthlyWarning, "monthly.globalWarnings"),
    units: expectArray(record.units, parseStudentBillingMonthlyUnit, "monthly.units")
  };
}

export async function fetchStudentBillingMonth(tenantSlug: string, serviceMonth: string): Promise<StudentBillingMonthlyListDto> {
  const month = parseStudentBillingServiceMonth(serviceMonth);
  return monthly(await apiGet(`${root(tenantSlug)}/months/${month}`));
}

export async function fetchStudentBillingMonthStudent(tenantSlug: string, serviceMonth: string, studentId: string): Promise<{ unit: StudentBillingMonthlyUnit; stale: boolean }> {
  const month = parseStudentBillingServiceMonth(serviceMonth); const student = parseStudentBillingIdentifier(studentId);
  const record = expectRecord(await apiGet(`${root(tenantSlug)}/months/${month}/students/${encodeURIComponent(student)}`), "monthlyStudent");
  exact(record, ["capabilities", "serviceMonth", "generation", "stale", "globalWarnings", "unit"], "monthlyStudent"); capabilities(record.capabilities);
  expectArray(record.globalWarnings, parseStudentBillingMonthlyWarning, "monthlyStudent.globalWarnings");
  return { unit: parseStudentBillingMonthlyUnit(record.unit), stale: expectBoolean(record.stale, "monthlyStudent.stale") };
}

export async function generateStudentBillingMonth(tenantSlug: string, serviceMonth: string, request: GenerateStudentBillingMonthlyDraftRequest): Promise<StudentBillingMonthlyListDto> {
  const month = parseStudentBillingServiceMonth(serviceMonth); const parsed = parseGenerateStudentBillingMonthlyDraftRequest(request);
  const response = expectRecord(await apiPostWithHeaders(`${root(tenantSlug)}/months/${month}/generate`, parsed, headers()), "monthlyGenerate");
  exact(response, ["replayed", "capabilities", "serviceMonth", "generation", "stale", "globalWarnings", "units"], "monthlyGenerate");
  return monthly(Object.fromEntries(["capabilities", "serviceMonth", "generation", "stale", "globalWarnings", "units"].map((key) => [key, response[key]])));
}

export async function previewStudentBillingEventChargeTargets(tenantSlug: string, eventId: string): Promise<StudentBillingEventChargeTargetPreviewDto> {
  const event = parseStudentBillingIdentifier(eventId);
  const response = expectRecord(await apiPostWithHeaders(`${root(tenantSlug)}/event-fees/${encodeURIComponent(event)}/charge-targets/preview`, {}, headers()), "eventTargetPreview");
  exact(response, ["capabilities", "preview"], "eventTargetPreview"); capabilities(response.capabilities);
  const value = expectRecord(response.preview, "eventTargetPreview.preview"); exact(value, ["eventId", "eventRevision", "candidates", "targetFingerprint"], "eventTargetPreview.preview");
  return { eventId: parseStudentBillingIdentifier(value.eventId), eventRevision: positiveInteger(value.eventRevision, "eventTargetPreview.preview.eventRevision"), candidates: expectArray(value.candidates, (item, path) => { const entry = expectRecord(item, path); exact(entry, ["studentId", "displayName"], path); return { studentId: parseStudentBillingIdentifier(entry.studentId), displayName: expectString(entry.displayName, `${path}.displayName`) }; }, "eventTargetPreview.preview.candidates"), targetFingerprint: fingerprint(value.targetFingerprint, "eventTargetPreview.preview.targetFingerprint") };
}

export async function putStudentBillingEventChargeTargets(tenantSlug: string, eventId: string, request: PutStudentBillingEventChargeTargetRequest): Promise<void> {
  const event = parseStudentBillingIdentifier(eventId); const parsed = parsePutStudentBillingEventChargeTargetRequest(request);
  await apiPutWithHeaders(`${root(tenantSlug)}/event-fees/${encodeURIComponent(event)}/charge-targets`, parsed, headers());
}
